import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { getIncompleteVouchers } from '../../../services/dashboardService';
import { VoucherItem } from '../../../types/dashboard';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const IncompleteVouchersWidget: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'receipt' | 'payment'>('receipt');
  const [receiptData, setReceiptData] = useState<VoucherItem[]>([]);
  const [paymentData, setPaymentData] = useState<VoucherItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [receipts, payments] = await Promise.all([
        getIncompleteVouchers('receipt'),
        getIncompleteVouchers('payment')
      ]);
      setReceiptData(receipts);
      setPaymentData(payments);
      setError(null);
    } catch (err) {
      setError('فشل في تحميل السندات الغير مكتملة');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewVoucher = (voucher: VoucherItem) => {
    // استخدام المسار الصحيح بناءً على نوع السند
    const path = voucher.type === 'receipt'
      ? `/finance/receipts/edit/${voucher._id}`
      : `/finance/payments/edit/${voucher._id}`;
    navigate(path);
  };

  const currentData = activeTab === 'receipt' ? receiptData : paymentData;

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', p: 3, minHeight: 300 }}>
        <Typography color="error">{error}</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderTop: '4px solid #f39c12',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              mr: 2
            }}
          >
            💰
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 0.5 }}>
              السندات الغير مكتملة
            </Typography>
            <Typography variant="body2" sx={{ color: '#95a5a6' }}>
              سندات الصرف والقبض
            </Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                سندات القبض
                <Chip 
                  label={receiptData.length} 
                  size="small" 
                  sx={{ bgcolor: '#ebf5fb', color: '#3498db', fontWeight: 'bold' }} 
                />
              </Box>
            }
            value="receipt"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                سندات الصرف
                <Chip 
                  label={paymentData.length} 
                  size="small" 
                  sx={{ bgcolor: '#fef5e7', color: '#f39c12', fontWeight: 'bold' }} 
                />
              </Box>
            }
            value="payment"
          />
        </Tabs>

        {/* Voucher List */}
        {currentData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h3" sx={{ opacity: 0.3, mb: 2 }}>✅</Typography>
            <Typography color="textSecondary">
              لا توجد {activeTab === 'receipt' ? 'سندات قبض' : 'سندات صرف'} غير مكتملة
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 250, overflow: 'auto' }}>
            {currentData.slice(0, 5).map((voucher) => (
              <ListItem
                key={voucher._id}
                sx={{
                  bgcolor: '#f8f9fa',
                  borderRadius: 2,
                  mb: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#e9ecef',
                    transform: 'translateX(-5px)'
                  }
                }}
                onClick={() => handleViewVoucher(voucher)}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                      {activeTab === 'receipt' ? 'سند قبض' : 'سند صرف'} #{voucher.voucherNumber}
                      {voucher.branchName && (
                        <Chip
                          label={voucher.branchName}
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#e3f2fd',
                            color: '#1976d2'
                          }}
                        />
                      )}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: '#95a5a6' }}>
                      📅 {format(new Date(voucher.date), 'yyyy-MM-dd', { locale: ar })} | {voucher.fromTo}
                    </Typography>
                  }
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#f39c12' }}>
                  {voucher.amount.toLocaleString('ar-SA')} ر.س
                </Typography>
              </ListItem>
            ))}
          </List>
        )}

        {currentData.length > 5 && (
          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/finance')}
            sx={{
              mt: 2,
              borderColor: '#f39c12',
              color: '#f39c12',
              fontWeight: 'bold',
              '&:hover': {
                borderColor: '#e67e22',
                bgcolor: 'rgba(243, 156, 18, 0.05)'
              }
            }}
          >
            عرض جميع السندات ({currentData.length})
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default IncompleteVouchersWidget;